import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { ArrowRight, User, Music, Calendar, Radio, Banknote, Palette, LogOut, Globe, AlertCircle, X, Check, MessageCircle, Circle, Target, MoreHorizontal, ListMusic, Bell, Gift, Bot, TrendingUp, Wallet } from 'lucide-react';
import LiveStatusControls from '@/components/LiveStatusControls';
import { useMobile } from '@/features/shared/contexts/MobileContext';
import { useArtistLiveStatus } from '@/features/artist-mode/contexts/ArtistLiveStatusContext';
import { doc, onSnapshot, collection, query, where, getCountFromServer, getDocs, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useOfflineCountdown } from '@/hooks/useOfflineCountdown';
import MobileAnimatedModal from '@/features/shared/components/MobileAnimatedModal';
import MobileOfflineCountdown from '@/features/shared/components/MobileOfflineCountdown';
import AddSongsRequiredModal from '@/components/AddSongsRequiredModal';
import { useToast } from '@/components/ui/use-toast';
import { useModalOptimization } from '@/hooks/useModalOptimization';
import {
  isAddSongWizardCompleted,
  ensureWizardDoneIfHasSongs,
  ADD_SONG_WIZARD_DISMISSED_KEY,
} from '@/lib/addSongWizardState';

// ✅ IMPORT THE NEW FIREBASE AUTH CONTEXT AND DB CONNECTION
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatMoney, DEFAULT_CURRENCY } from '@/lib/currency';
import {
  isPaidSongRequestForArtistLedger,
  payoutDocMatchesArtistRail,
  PLATFORM_COMMISSION,
  applyAdminLedgerDisplay,
} from '@/lib/payoutLedger';
import QRCode from 'qrcode.react';

// Import the panel components (we will refactor these one by one)
import LiveDashboardPage from './LiveDashboardPage';
import ProfilePage from './ProfilePage';
import SetlistPage from './SetlistPage';
import CalendarPage from './CalendarPage';
import PayoutsPage from './PayoutsPage';
import CreativeAssetsPage from './CreativeAssetsPage';
import ReferralDashboard from '@/features/shared/components/ReferralDashboard';
import ArtistCompetitionPage from './ArtistCompetitionPage';
import StorePage from './StorePage';
import UserNotificationCenter from '@/features/shared/components/UserNotificationCenter';

const SidebarWrapper = ({ children, className = "" }) => (
  <div className={`h-full overflow-auto ${className}`}>
    {children}
  </div>
);

const AdminDashboard = () => {
  console.log('🔍 AdminDashboard: Component rendered');
  
  // ✅ USEAUTH NOW PULLS FROM FIREBASE
  const { user, profile, signOut, loading: authLoading } = useAuth();
  const currencyContext = useCurrency();
  const payoutCcy = profile?.currency || DEFAULT_CURRENCY;
  const format = currencyContext?.format || ((amount) => formatMoney(amount ?? 0, payoutCcy));
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const mobile = useMobile();
  const localLiveStatus = useArtistLiveStatus();
  const modalOptimization = useModalOptimization();
  const { toast } = useToast();
  const [liveStatus, setLiveStatus] = useState({ isLive: false });
  
  console.log('🔍 AdminDashboard: State initialized', { 
    user: !!user, 
    profile: !!profile, 
    authLoading, 
    mobile: mobile.isMobile 
  });

  const [songsCount, setSongsCount] = useState(0);
  const [songs, setSongs] = useState([]);
  const [loadingSongs, setLoadingSongs] = useState(true);
  
  const [requests, setRequests] = useState([]);
  const [tips, setTips] = useState([]);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [currentPanel, setCurrentPanel] = useState('/admin/live-dashboard');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [isTrialMode, setIsTrialMode] = useState(false); // Track trial mode for button glow
  const [showCatalogueModal, setShowCatalogueModal] = useState(false);
  const [showPayoutsModal, setShowPayoutsModal] = useState(false);
  const [showQuickTourModal, setShowQuickTourModal] = useState(false);
  const [catalogueGlow, setCatalogueGlow] = useState(false);
  const [showAddSongsModal, setShowAddSongsModal] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const [isGoingLive, setIsGoingLive] = useState(false);
  const [competitionEnabled, setCompetitionEnabled] = useState(false);
  const [competitionPrize, setCompetitionPrize] = useState('');
  const [goLivePillPulse, setGoLivePillPulse] = useState(false);
  const {
    showOfflineCountdown,
    offlineCountdown,
    startOfflineCountdown,
    cancelOfflineCountdown
  } = useOfflineCountdown();

  // Calculate total earnings
  const calculateTotalEarnings = () => {
    // amount_paid already includes tips for song requests, so we don't double-count
    const totalRequestEarnings = requests.reduce((sum, req) => sum + (req.amount_paid || 0), 0);
    // Only add standalone tips (not associated with song requests)
    const totalStandaloneTips = tips.reduce((sum, tip) => sum + (tip.amount || 0), 0);
    return totalRequestEarnings + totalStandaloneTips;
  };

  const totalEarnings = calculateTotalEarnings();

  // Same available-to-request math as the full Payouts page (artist share − paid − reserved).
  const availableToRequest = useMemo(() => {
    const paidRequests = requests.filter((req) => isPaidSongRequestForArtistLedger(req, profile || {}));
    const gross = paidRequests.reduce((sum, req) => {
      const totalPaid = Number(req.total_paid);
      if (!Number.isNaN(totalPaid) && totalPaid > 0) return sum + totalPaid;
      return sum + Number(req.amount_paid || 0);
    }, 0);
    const artistShare = gross * (1 - PLATFORM_COMMISSION);
    const isUsArtist = String(profile?.country || '').toUpperCase() === 'US';
    const railPayouts = payoutRequests.filter((p) => payoutDocMatchesArtistRail(p, profile || {}));
    const paidOut = isUsArtist
      ? railPayouts.filter((p) => p.status === 'paid').reduce((s, p) => s + Number(p.amount || 0), 0)
      : Number(profile?.payouts_paid_total || 0);
    const reserved = railPayouts
      .filter((p) => p.status === 'pending' || p.status === 'approved')
      .reduce((s, p) => s + Number(p.amount || 0), 0);
    const display = applyAdminLedgerDisplay(
      { gross, artistShare, paidOut, reserved },
      profile || {}
    );
    return display.currentBalance;
  }, [requests, payoutRequests, profile]);

  // Check notification permission
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  // Handle URL parameters for mobile modals and live status
  useEffect(() => {
    const tour = searchParams.get('tour');
    const start = searchParams.get('start');
    const duration = searchParams.get('duration');
    const offline = searchParams.get('offline');
    
    if (tour === 'true' && mobile.isMobile) {
      setShowQuickTourModal(true);
    }
    
    if (start === 'true' && duration) {
      // Navigate to live dashboard with start parameters
      navigate(`/admin/live-dashboard?start=true&duration=${duration}`);
    } else if (offline === 'true') {
      // Navigate to live dashboard with offline parameter
      navigate('/admin/live-dashboard?offline=true');
    }
  }, [searchParams, mobile.isMobile, navigate]);

  // Fetch requests and tips data for earnings calculation
  useEffect(() => {
    if (!user?.uid) return;

    // Set up real-time listeners for requests and tips
    const requestsQuery = query(
      collection(db, 'requests'),
      where('artist_id', '==', user.uid)
    );

    const requestsUnsubscribe = onSnapshot(requestsQuery, (snapshot) => {
      const requestsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log("🔄 Requests updated in AdminDashboard:", requestsData.length, "requests");
      setRequests(requestsData);
    }, (error) => {
      console.error('Error in requests listener (AdminDashboard):', error);
    });

    const tipsQuery = query(
      collection(db, 'tips'),
      where('artist_id', '==', user.uid)
    );

    const tipsUnsubscribe = onSnapshot(tipsQuery, (snapshot) => {
      const tipsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      console.log("🔄 Tips updated in AdminDashboard:", tipsData.length, "tips");
      setTips(tipsData);
    }, (error) => {
      console.error('Error in tips listener (AdminDashboard):', error);
    });

    const payoutsQuery = query(
      collection(db, 'artist_payout_requests'),
      where('artist_id', '==', user.uid)
    );

    const payoutsUnsubscribe = onSnapshot(payoutsQuery, (snapshot) => {
      setPayoutRequests(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (error) => {
      console.error('Error in payout requests listener (AdminDashboard):', error);
    });

    return () => {
      requestsUnsubscribe();
      tipsUnsubscribe();
      payoutsUnsubscribe();
    };
  }, [user?.uid]);

  // One-time autoplay of tutorial immediately after signup on mobile
  useEffect(() => {
    if (user && mobile.isMobile && !authLoading) {
      const justSignedUp = localStorage.getItem('ysr_just_signed_up') === 'true';
      if (justSignedUp) {
        // Clear the flag so it only happens once
        localStorage.removeItem('ysr_just_signed_up');
        // Trigger onboarding and navigate directly to Live Dashboard so it auto-plays once
        localStorage.setItem('ysr_trigger_onboarding', 'true');
        navigate('/admin/live-dashboard');
      }
    }
  }, [user, mobile.isMobile, authLoading]);

  // One-time onboarding trigger for desktop users after signup
  useEffect(() => {
    console.log('🔍 AdminDashboard: Checking desktop onboarding trigger', { 
      user: !!user, 
      isMobile: mobile.isMobile, 
      authLoading,
      justSignedUp: localStorage.getItem('ysr_just_signed_up'),
      windowWidth: window.innerWidth
    });
    
    if (user && !authLoading) {
      const justSignedUp = localStorage.getItem('ysr_just_signed_up') === 'true';
      console.log('🔍 AdminDashboard: User detected, justSignedUp:', justSignedUp, 'isMobile:', mobile.isMobile);
      
      if (justSignedUp) {
        console.log('🎯 AdminDashboard: Triggering onboarding for user!');
        // Clear the flag so it only happens once
        localStorage.removeItem('ysr_just_signed_up');
        // Trigger onboarding for desktop users
        localStorage.setItem('ysr_trigger_onboarding', 'true');
        console.log('🎯 AdminDashboard: Set ysr_trigger_onboarding flag - LiveDashboardPage should detect this');
      }
    }
  }, [user, mobile.isMobile, authLoading, navigate]);


  // Check for trial mode from localStorage (set by LiveDashboardPage)
  useEffect(() => {
    const checkTrialMode = () => {
      const trialActive = localStorage.getItem('trial_mode_active') === 'true';
      setIsTrialMode(trialActive);
    };
    
    checkTrialMode();
    const interval = setInterval(checkTrialMode, 100); // Check every 100ms for responsiveness
    
    return () => clearInterval(interval);
  }, []);
  
  // Check for Go Live pill pulse state
  useEffect(() => {
    const checkPulse = () => {
      const pulseActive = localStorage.getItem('go_live_pill_pulse') === 'true';
      setGoLivePillPulse(pulseActive);
    };
    
    checkPulse();
    const interval = setInterval(checkPulse, 100);
    
    return () => clearInterval(interval);
  }, []);



  // Check if onboarding is completed and set catalogue glow based on song count
  useEffect(() => {
    const checkStatus = () => {
      const onboardingCompleted = localStorage.getItem('ysr_onboarding_completed') === 'true';
      const catalogueGlowDismissed = localStorage.getItem('ysr_catalogue_glow_dismissed') === 'true';
      const justCompletedOnboarding = localStorage.getItem('ysr_just_completed_onboarding') === 'true';
      
      setHasCompletedOnboarding(onboardingCompleted);
      
      // Set glow if onboarding completed, not dismissed, and less than 5 songs
      if (onboardingCompleted && !catalogueGlowDismissed && songsCount < 5) {
        setCatalogueGlow(true);
        
        // Safari-specific glow enhancement
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const isMobileSafari = isSafari && /iPhone|iPad|iPod/.test(navigator.userAgent);
        if (isSafari) {
          // Add a more visible Safari-specific glow effect
          setTimeout(() => {
            const catalogueButton = document.querySelector('.catalogue-glow');
            if (catalogueButton) {
              catalogueButton.style.setProperty('--safari-glow', '1');
              catalogueButton.classList.add('safari-glow-enhanced');
              
              // Extra enhancement for Mobile Safari
              if (isMobileSafari) {
                catalogueButton.classList.add('mobile-safari-glow');
                // Force a more visible effect for Mobile Safari
                catalogueButton.style.setProperty('--mobile-safari-glow', '1');
              }
            }
          }, 100);
        }
        
        // Show helpful message if they just completed onboarding
        if (justCompletedOnboarding && mobile.isMobile) {
          localStorage.removeItem('ysr_just_completed_onboarding');
          // Small delay to let the page load
          setTimeout(() => {
            // Don't use toast on mobile, just rely on the glow effect
          }, 500);
        }
      } else {
        setCatalogueGlow(false);
      }
    };
    
    // Check immediately
    checkStatus();
    
    // Listen for storage changes
    const handleStorageChange = (e) => {
      if (e.key === 'ysr_onboarding_completed' || e.key === 'ysr_catalogue_glow_dismissed') {
        checkStatus();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically in case of same-tab changes
    const interval = setInterval(checkStatus, 1000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [mobile.isMobile, songsCount]);

  // Real-time live status listener
  useEffect(() => {
    if (!user?.uid) return;

    const liveStatusUnsubscribe = onSnapshot(doc(db, "live_status", user.uid), (docSnap) => {
      if (docSnap.exists()) {
        const statusData = docSnap.data();
        setLiveStatus({ 
          isLive: statusData.is_live,
          session_id: statusData.session_id || null,
          accepting_new_requests: statusData.accepting_new_requests !== false // Default to true if not set
        });
      } else {
        setLiveStatus({ isLive: false, session_id: null, accepting_new_requests: true });
      }
    }, (error) => {
      console.error("Error listening to live status:", error);
      setLiveStatus({ isLive: false, session_id: null, accepting_new_requests: true });
    });

    return () => liveStatusUnsubscribe();
  }, [user?.uid]);

  /**
   * Unplayed requests in the current session. Counts any non-'played' status,
   * because paid requests can arrive with statuses other than 'pending' and
   * must never be hidden from the artist.
   */
  const pendingRequestsCount = useMemo(() => {
    if (!liveStatus.isLive || !liveStatus.session_id) return 0;
    return requests.filter(r =>
      r.session_id === liveStatus.session_id &&
      (r.status || '').toLowerCase() !== 'played'
    ).length;
  }, [requests, liveStatus.isLive, liveStatus.session_id]);


  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };


  const handlePushNotificationToggle = async () => {
    // Push notifications removed - service workers disabled
    toast({
      title: "Push Notifications Unavailable",
      description: "Push notifications are currently disabled",
      variant: "destructive"
    });
  };
  
  // OLD CODE REMOVED - Push notification functionality disabled
  const oldPushNotificationCode = () => {
    try {
      // This code has been removed
    } catch (error) {
      console.error('❌ Error handling push notification toggle:', error);
      toast({
        title: "Error",
        description: "Failed to enable notifications",
        variant: "destructive"
      });
    }
  };


  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };
  
  const handlePanelChange = (panelPath) => {
    const qIndex = panelPath.indexOf('?');
    const pathOnly = qIndex >= 0 ? panelPath.slice(0, qIndex) : panelPath;
    const query = qIndex >= 0 ? panelPath.slice(qIndex) : '';

    if (currentPanel === pathOnly && !query) return;

    if (pathOnly === '/admin/catalogue') {
      try {
        localStorage.removeItem(ADD_SONG_WIZARD_DISMISSED_KEY);
      } catch (_) {
        /* ignore */
      }
      const onboardingCompleted = localStorage.getItem('ysr_onboarding_completed') === 'true';
      if (
        onboardingCompleted &&
        !isAddSongWizardCompleted(profile, songsCount) &&
        songsCount < 5
      ) {
        localStorage.setItem('ysr_show_song_wizard', 'true');
      }
    }

    if (pathOnly === '/admin/live-dashboard') {
      localStorage.setItem('ysr_clicked_live_nav', 'true');
    }

    if (currentPanel !== pathOnly) {
      setCurrentPanel(pathOnly);
    }
    navigate(pathOnly + query);
  };

  // Mobile navigation handlers
  const handleMobileNavigationClick = (item) => {
    if (item.isMore) {
      setShowMoreMenu(!showMoreMenu);
      return;
    }
    setShowMoreMenu(false);
    
    // Handle mobile-specific modal actions
    if (mobile.isMobile) {
      switch (item.id) {
        case 'profile':
          setShowProfileModal(!showProfileModal); // Toggle profile modal
          return;
        case 'catalogue':
          // Use handleCatalogueClick to navigate and show wizard if needed
          handleCatalogueClick();
          return;
        case 'payouts':
          setShowPayoutsModal(!showPayoutsModal); // Toggle payouts modal
          return;
        case 'live':
          localStorage.setItem('ysr_clicked_live_nav', 'true');
          // Always navigate to live dashboard, regardless of current page
          handlePanelChange('/admin/live-dashboard');
          return;
        default:
          break;
      }
    }
    
    // Fallback to normal navigation for desktop or other items
    if (item.path) {
      if (item.isExternal) {
        window.open(item.path, '_blank');
      } else {
        handlePanelChange(item.path);
      }
    }
  };

  const handleMoreMenuClick = (item) => {
    if (item.isComingSoon) {
      setShowMoreMenu(false);
      return;
    }
    if (item.isSpecial && item.id === 'melody') {
      window.dispatchEvent(new CustomEvent('openMelody', { detail: {}, bubbles: true }));
      setShowMoreMenu(false);
      return;
    }
    setShowMoreMenu(false);
    if (item.isExternal) {
      window.open(item.path, '_blank');
    } else {
      handlePanelChange(item.path);
    }
  };

  const handleCatalogueClick = () => {
    if (catalogueGlow) {
      setCatalogueGlow(false);
      localStorage.setItem('ysr_catalogue_glow_dismissed', 'true');
    }
    handlePanelChange('/admin/catalogue');
  };



  // ✅ REFACTORED TO USE REAL-TIME SONG COUNT LISTENER
  useEffect(() => {
    if (!user || !user.uid) return;
    
    console.log('🔍 Setting up real-time songs count listener for user:', user.uid);
    setLoadingSongs(true);

    // Real-time listener for songs collection
    const songsCollection = collection(db, 'songs');
    const songsQuery = query(songsCollection, where('user_id', '==', user.uid));
    
    const unsubscribe = onSnapshot(songsQuery, (querySnapshot) => {
      try {
        const activeSongs = querySnapshot.docs
          .filter(doc => !doc.data().archived)
          .map(doc => ({ id: doc.id, ...doc.data() }));
        setSongs(activeSongs);
        setSongsCount(activeSongs.length);
        void ensureWizardDoneIfHasSongs(db, user.uid, activeSongs.length);
        console.log('✅ Real-time songs count updated:', activeSongs.length, 'songs found');
      } catch (err) {
        console.error('💥 Exception in real-time songs count:', err);
        setSongs([]);
        setSongsCount(0);
      } finally {
        setLoadingSongs(false);
      }
    }, (error) => {
      console.error('💥 Error in real-time songs count listener:', error);
      // Don't show error for permission issues during initial setup
      if (!error.message?.includes('Missing or insufficient permissions')) {
        console.error('Unexpected error in songs count listener:', error);
      }
      setSongs([]);
      setSongsCount(0);
      setLoadingSongs(false);
    });

    // Cleanup function
    return () => {
      console.log('🧹 Cleaning up real-time songs count listener');
      unsubscribe();
    };
  }, [user?.uid]);

  const needsUrlSetup = !profile?.url_slug;
  const isPlatformAdmin = profile?.account_type === 'admin' || profile?.is_admin === true;

  // Listen for competition settings in real-time
  useEffect(() => {
    if (!db || !user?.uid) return;

    const competitionRef = doc(db, 'competitions', 'current');
    const unsubscribe = onSnapshot(competitionRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setCompetitionEnabled(!!data.enabled);
        setCompetitionPrize(data.prize || '');
      } else {
        setCompetitionEnabled(false);
        setCompetitionPrize('');
      }
    }, (error) => {
      console.error('Error listening to competition settings:', error);
    });

    return () => unsubscribe();
  }, [db, user?.uid]);

  // More menu items for mobile
  const moreMenuItems = useMemo(() => [
    { id: 'referrals', label: 'Refer & Reward', icon: Gift, path: '/admin/referrals' },
    { id: 'melody', label: 'Melody', icon: Bot, path: null, isSpecial: true },
    ...(competitionEnabled ? [{
      id: 'competition',
      label: 'Competition',
      icon: TrendingUp,
      path: '/admin/competition',
      isFullWidth: true,
      tickerText: competitionPrize || 'Win an all expenses trip to the home of music; Nashville!'
    }] : [])
  ], [competitionEnabled, competitionPrize]);

  const sidebarItems = useMemo(() => {
    const items = [
      { title: "Live Dashboard", description: "Run live sessions & requests", color: "from-red-500 to-orange-500", icon: Radio, path: "/admin/live-dashboard", disabled: needsUrlSetup },
      { title: "Profile Management", description: "Update profile & QR code", color: "from-blue-500 to-cyan-500", icon: User, path: "/admin/profile", disabled: false },
      { title: "Catalogue Management", description: "Manage catalogue, pricing & tags", color: "from-purple-500 to-pink-500", icon: Music, path: "/admin/catalogue", disabled: needsUrlSetup },
      { title: "Calendar Management", description: "Schedule gigs & events", color: "from-green-500 to-emerald-500", icon: Calendar, path: "/admin/calendar", disabled: needsUrlSetup },
      { title: "Payouts", description: "View earnings & request payouts", color: "from-yellow-500 to-lime-500", icon: Banknote, path: "/admin/payouts", disabled: needsUrlSetup },
      { title: "Refer & Reward", description: "Refer artists and earn request points", color: "from-pink-500 to-rose-500", icon: Gift, path: "/admin/referrals", disabled: false },
      { title: "Promo Tools", description: "Creative assets & promo tools", color: "from-indigo-500 to-purple-500", icon: Palette, path: "/admin/creative-assets", disabled: needsUrlSetup },
      { title: "Public Page", description: "Open your public page", color: "from-gray-500 to-gray-600", icon: Globe, path: `https://yoursongrequest.com/${profile?.url_slug}`, isExternal: true, disabled: needsUrlSetup },
    ];
    // Desktop artist dashboard: hide Promo Tools (assets live under Live / share sheet). Keep for platform admins.
    if (!mobile.isMobile && !isPlatformAdmin) {
      return items.filter((item) => item.path !== '/admin/creative-assets');
    }
    return items;
  }, [needsUrlSetup, profile?.url_slug, mobile.isMobile, isPlatformAdmin]);
  
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/admin/')) {
        setCurrentPanel(path);
    }
  }, [location.pathname]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const needsSetlistSetup = !!profile?.url_slug && songsCount < 5;
  const hasEnoughSongs = songsCount >= 5;
  const isAgentManaged = profile?.agent_id && profile?.agent_payout_preference === '80_20';

  // Mobile navigation items
  let mobileNavigationItems = mobile.isMobile ? [
    { id: 'profile', label: 'Profile', icon: User, path: '/admin/profile' },
    { id: 'catalogue', label: 'Catalogue', icon: Music, path: '/admin/catalogue' },
    { id: 'live', label: 'Live', icon: Radio, path: '/admin/live-dashboard' },
    { id: 'payouts', label: 'Payouts', icon: Banknote, path: '/admin/payouts' },
    { id: 'more', label: 'More', icon: MoreHorizontal, path: null, isMore: true },
  ] : [];

  if (isAgentManaged && mobile.isMobile) {
    mobileNavigationItems = mobileNavigationItems.filter(item => item.id !== 'payouts');
  }

  const activeIndex = sidebarItems.findIndex(item => item.path === currentPanel);
  const isMobileLivePanel = mobile.isMobile && currentPanel === '/admin/live-dashboard';

  // Deterministic highlight placement (no DOM measurements)
  const EXPANDED_ITEM_HEIGHT = 60; // px
  const EXPANDED_STEP = 68;        // item height + 8px gap
  const COLLAPSED_ITEM_HEIGHT = 48; // px
  const COLLAPSED_STEP = 56;        // item height + 8px gap
  const highlightY = activeIndex * (sidebarCollapsed ? COLLAPSED_STEP : EXPANDED_STEP);
  const highlightH = sidebarCollapsed ? COLLAPSED_ITEM_HEIGHT : EXPANDED_ITEM_HEIGHT;

  return (
    <>
      <Helmet>
        <title>Artist Dashboard - Your Song Request</title>
        <style>
          {`
            @-webkit-keyframes catalogueGlow {
              0%, 100% {
                -webkit-box-shadow: 0 0 20px rgba(168, 85, 247, 0.3);
                -webkit-transform: scale(1);
                background-color: rgba(168, 85, 247, 0.1);
                border: 2px solid rgba(168, 85, 247, 0.3);
              }
              50% {
                -webkit-box-shadow: 0 0 30px rgba(168, 85, 247, 0.8);
                -webkit-transform: scale(1.02);
                background-color: rgba(168, 85, 247, 0.2);
                border: 2px solid rgba(168, 85, 247, 0.6);
              }
            }
            @keyframes catalogueGlow {
              0%, 100% {
                box-shadow: 0 0 20px rgba(168, 85, 247, 0.3);
                transform: scale(1);
                background-color: rgba(168, 85, 247, 0.1);
                border: 2px solid rgba(168, 85, 247, 0.3);
              }
              50% {
                box-shadow: 0 0 30px rgba(168, 85, 247, 0.8);
                transform: scale(1.02);
                background-color: rgba(168, 85, 247, 0.2);
                border: 2px solid rgba(168, 85, 247, 0.6);
              }
            }
            .catalogue-glow {
              -webkit-animation: catalogueGlow 4s ease-in-out infinite;
              animation: catalogueGlow 4s ease-in-out infinite;
              -webkit-transform: translateZ(0);
              transform: translateZ(0);
              -webkit-backface-visibility: hidden;
              backface-visibility: hidden;
              -webkit-perspective: 1000;
              perspective: 1000;
            }
            /* Force hardware acceleration for Safari */
            .catalogue-glow * {
              -webkit-transform: translateZ(0);
              transform: translateZ(0);
            }
            /* Enhanced Safari glow effect */
            .safari-glow-enhanced {
              background: linear-gradient(45deg, rgba(168, 85, 247, 0.2), rgba(168, 85, 247, 0.4)) !important;
              border: 3px solid rgba(168, 85, 247, 0.8) !important;
              -webkit-box-shadow: 0 0 40px rgba(168, 85, 247, 0.9) !important;
              box-shadow: 0 0 40px rgba(168, 85, 247, 0.9) !important;
            }
            
            /* Mobile Safari specific glow enhancement */
            .mobile-safari-glow {
              background: linear-gradient(45deg, rgba(168, 85, 247, 0.3), rgba(168, 85, 247, 0.5)) !important;
              border: 4px solid rgba(168, 85, 247, 1) !important;
              -webkit-box-shadow: 
                0 0 20px rgba(168, 85, 247, 1),
                0 0 40px rgba(168, 85, 247, 0.8),
                0 0 60px rgba(168, 85, 247, 0.6) !important;
              box-shadow: 
                0 0 20px rgba(168, 85, 247, 1),
                0 0 40px rgba(168, 85, 247, 0.8),
                0 0 60px rgba(168, 85, 247, 0.6) !important;
              -webkit-animation: mobileSafariGlow 4s ease-in-out infinite !important;
              animation: mobileSafariGlow 4s ease-in-out infinite !important;
            }
            
            @-webkit-keyframes mobileSafariGlow {
              0%, 100% {
                -webkit-box-shadow: 
                  0 0 20px rgba(168, 85, 247, 1),
                  0 0 40px rgba(168, 85, 247, 0.8),
                  0 0 60px rgba(168, 85, 247, 0.6);
                -webkit-transform: scale(1);
                background: linear-gradient(45deg, rgba(168, 85, 247, 0.3), rgba(168, 85, 247, 0.5));
              }
              50% {
                -webkit-box-shadow: 
                  0 0 30px rgba(168, 85, 247, 1),
                  0 0 60px rgba(168, 85, 247, 0.9),
                  0 0 90px rgba(168, 85, 247, 0.7);
                -webkit-transform: scale(1.05);
                background: linear-gradient(45deg, rgba(168, 85, 247, 0.4), rgba(168, 85, 247, 0.6));
              }
            }
            
            @keyframes mobileSafariGlow {
              0%, 100% {
                box-shadow: 
                  0 0 20px rgba(168, 85, 247, 1),
                  0 0 40px rgba(168, 85, 247, 0.8),
                  0 0 60px rgba(168, 85, 247, 0.6);
                transform: scale(1);
                background: linear-gradient(45deg, rgba(168, 85, 247, 0.3), rgba(168, 85, 247, 0.5));
              }
              50% {
                box-shadow: 
                  0 0 30px rgba(168, 85, 247, 1),
                  0 0 60px rgba(168, 85, 247, 0.9),
                  0 0 90px rgba(168, 85, 247, 0.7);
                transform: scale(1.05);
                background: linear-gradient(45deg, rgba(168, 85, 247, 0.4), rgba(168, 85, 247, 0.6));
              }
            }
          `}
        </style>
      </Helmet>
      
      {/* Logout Confirmation Modal - Mobile Optimized */}
      <MobileAnimatedModal
        isOpen={mobile.isMobile && showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        title="Confirm Logout"
        {...modalOptimization.getOptimalModalProps()}
      >
        <div {...modalOptimization.getOptimalContentProps()}>
          <div className={`${modalOptimization.modalSettings.iconSize} bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4`}>
            <LogOut className={`${modalOptimization.modalSettings.iconSize} text-red-600`} />
          </div>
          
          <h4 className={`text-white font-semibold mb-2 text-center ${modalOptimization.modalSettings.textSize === 'text-xs' ? 'text-sm' : 'text-base'}`}>
            Are you sure you want to log out?
          </h4>
          
          <p className={`text-gray-400 text-center mb-6 ${modalOptimization.modalSettings.textSize}`}>
            You'll need to sign in again to access your dashboard.
          </p>
          
          <div className="space-y-3">
            <Button
              variant="outline"
              onClick={() => setShowLogoutConfirm(false)}
              className="w-full"
              {...modalOptimization.getOptimalButtonProps()}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleLogout}
              className="w-full"
              {...modalOptimization.getOptimalButtonProps()}
            >
              Logout
            </Button>
          </div>
        </div>
      </MobileAnimatedModal>
      
      {/* Desktop Logout Confirmation Dialog */}
      {!mobile.isMobile && (
        <Dialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
          <DialogContent className="bg-gray-900 border-gray-700 text-white">
            <DialogHeader>
              <DialogTitle>Confirm Logout</DialogTitle>
              <DialogDescription>
                Are you sure you want to log out? You'll need to sign in again to access your dashboard.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLogoutConfirm(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleLogout}>
                Logout
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      
      <div className="min-h-screen bg-black text-white">
        <div className="flex h-[100dvh]">
          <aside className={`${mobile.isMobile ? 'hidden' : sidebarCollapsed ? 'w-20' : 'w-80'} bg-gray-900/50 backdrop-blur-sm border-r border-gray-800 flex flex-col transition-all duration-300 ease-in-out relative`}>
            {/* Sidebar header */}
            <div className="p-4 border-b border-gray-800">
              {!sidebarCollapsed && (
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">YSR</span>
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-white font-semibold">Dashboard</h3>
                      <p className="text-gray-400 text-xs truncate">{profile?.artist_name || 'Artist'}</p>
                    </div>
                  </div>
                  <div className="flex-shrink-0 scale-90 origin-right">
                    <UserNotificationCenter />
                  </div>
                </div>
              )}
              {sidebarCollapsed && (
                <div className="flex flex-col items-center space-y-2">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">YSR</span>
                  </div>
                  <div className="scale-90">
                    <UserNotificationCenter />
                  </div>
                </div>
              )}
            </div>

            {/* Live Status Controls - Always visible when live or when setup is complete */}
            {!needsUrlSetup && !needsSetlistSetup && (
              <LiveStatusControls 
                sidebarCollapsed={sidebarCollapsed} 
                songs={songs}
                pendingRequestsCount={pendingRequestsCount}
              />
            )}
            
            
            {/* Sidebar navigation */}
            <div className="p-4 flex-1 min-h-0 overflow-y-auto">
                <nav className="relative mt-2 pb-4">
                    <motion.div
                        className="absolute inset-x-0 bg-gradient-to-r from-blue-600/20 to-indigo-600/20 border border-blue-500/30 shadow-lg rounded-lg"
                        animate={{ y: highlightY, height: highlightH }}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                    <div className="relative space-y-2">
                        {sidebarItems.map((item, index) => (
                            <button
                                key={item.path}
                                onClick={() => !item.disabled && (item.isExternal ? window.open(item.path, '_blank') : handlePanelChange(item.path))}
                                className={`relative block w-full text-left rounded-lg transition-all duration-200 group ${sidebarCollapsed ? 'p-3 h-12' : 'p-3 h-[60px]'} ${item.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800/50'}`}
                            >
                                <div className="flex items-center space-x-3">
                                    <div className={`w-9 h-9 rounded-md bg-gradient-to-r ${item.color} flex items-center justify-center ${sidebarCollapsed ? 'mx-auto' : ''}`}>
                                        <item.icon className="w-4 h-4 text-white" />
                                    </div>
                                    {!sidebarCollapsed && (
                                        <div className="flex flex-col">
                                            <span className={`font-medium ${item.disabled ? 'text-gray-500' : currentPanel === item.path ? 'text-white' : 'text-gray-200 group-hover:text-white'}`}>{item.title}</span>
                                            {currentPanel === item.path && (
                                                <span className={`text-xs ${item.disabled ? 'text-gray-600' : 'text-gray-400'} truncate max-w-[200px]`}>{item.description}</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                </nav>
            </div>
            
            {/* Sidebar footer with Melody and logout */}
            <div className="p-4 border-t border-gray-800 space-y-2">
                <button
                    type="button"
                    onClick={() => window.dispatchEvent(new CustomEvent('openMelody', { detail: {}, bubbles: true }))}
                    className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all duration-200 hover:bg-purple-900/20 hover:border-purple-700/50 border border-transparent ${sidebarCollapsed ? 'justify-center' : ''}`}
                >
                    <MessageCircle className={`w-5 h-5 text-purple-400 ${sidebarCollapsed ? 'mx-auto' : ''}`} />
                    {!sidebarCollapsed && <span className="text-purple-400 font-medium">Melody</span>}
                </button>
                <button
                    onClick={() => setShowLogoutConfirm(true)}
                    className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all duration-200 hover:bg-red-900/20 hover:border-red-700/50 border border-transparent ${sidebarCollapsed ? 'justify-center' : ''}`}
                >
                    <LogOut className={`w-5 h-5 text-red-400 ${sidebarCollapsed ? 'mx-auto' : ''}`} />
                    {!sidebarCollapsed && <span className="text-red-400 font-medium">Logout</span>}
                </button>
            </div>
            
            {/* Sidebar collapse toggle */}
            <button
                onClick={toggleSidebar}
                className="absolute -right-3 top-1/2 transform -translate-y-1/2 bg-gray-800 hover:bg-gray-700 p-1 rounded-full border border-gray-700"
            >
                <ArrowRight className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${!sidebarCollapsed ? 'rotate-180' : ''}`} />
            </button>
          </aside>
          <main className={`${mobile.isMobile ? 'w-full' : 'flex-1'} ${isMobileLivePanel ? 'overflow-hidden' : 'overflow-y-auto'}`}>
            <div className={mobile.isMobile ? 'p-0 h-full' : 'p-6'}>
                {/* Welcome messages and action required cards */}
                {needsUrlSetup && (
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }} 
                        animate={{ opacity: 1, y: 0 }} 
                        className="mb-6 p-6 bg-blue-900/20 border border-blue-700/50 rounded-lg"
                    >
                        <div className="flex items-start space-x-4">
                            <AlertCircle className="w-6 h-6 text-blue-400 mt-1 flex-shrink-0" />
                            <div>
                                <h3 className="text-lg font-semibold text-blue-300 mb-2">Setup Required: Artist URL</h3>
                                <p className="text-blue-200 mb-4">
                                    You need to set up your artist URL before you can access most dashboard features. 
                                    This will be your public page where fans can request songs.
                                </p>
                                <Button 
                                    onClick={() => handlePanelChange('/admin/profile')}
                                    className="bg-blue-600 hover:bg-blue-700 text-white"
                                >
                                    Set Up Profile
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                )}
                
                {needsSetlistSetup && !mobile.isMobile && (
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }} 
                        animate={{ opacity: 1, y: 0 }} 
                        className="mb-6 p-6 bg-yellow-900/20 border border-yellow-700/50 rounded-lg"
                    >
                        <div className="flex items-start space-x-4">
                            <Music className="w-6 h-6 text-yellow-400 mt-1 flex-shrink-0" />
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-yellow-300 mb-2">Setup Required: Song Catalogue</h3>
                                <p className="text-yellow-200 mb-4">
                                    Your artist URL is set up, but you need to add songs to your catalogue. 
                                    Add at least 5 songs so fans can request them during your live performances.
                                </p>
                                <div className="flex items-center space-x-4">
                                    <Button 
                                        onClick={() => handlePanelChange('/admin/catalogue')}
                                        className="bg-yellow-600 hover:bg-yellow-700 text-white"
                                    >
                                        Add Songs
                                    </Button>
                                    <div className="flex items-center space-x-2 text-yellow-300">
                                        <span className="text-sm font-medium">Progress:</span>
                                        <div className="flex items-center space-x-1">
                                            <span className="text-lg font-bold">{songsCount}</span>
                                            <span className="text-sm">/</span>
                                            <span className="text-lg font-bold">5</span>
                                            <span className="text-sm ml-1">songs</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
                

                <div className={mobile.isMobile ? 'h-full overflow-hidden' : 'flex-1 overflow-hidden'}>
                    {currentPanel === '/admin/live-dashboard' && <SidebarWrapper><LiveDashboardPage /></SidebarWrapper>}
                    {currentPanel === '/admin/profile' && <SidebarWrapper><ProfilePage /></SidebarWrapper>}
                    {currentPanel === '/admin/catalogue' && <SidebarWrapper><SetlistPage /></SidebarWrapper>}
                    {currentPanel === '/admin/calendar' && <SidebarWrapper><CalendarPage /></SidebarWrapper>}
                    {currentPanel === '/admin/payouts' && <SidebarWrapper><PayoutsPage /></SidebarWrapper>}
                    {currentPanel === '/admin/referrals' && <SidebarWrapper><ReferralDashboard /></SidebarWrapper>}
                    {currentPanel === '/admin/competition' && <SidebarWrapper><ArtistCompetitionPage /></SidebarWrapper>}
                    {currentPanel === '/admin/creative-assets' && <SidebarWrapper><CreativeAssetsPage /></SidebarWrapper>}
                    {currentPanel === '/admin/store' && <SidebarWrapper><StorePage /></SidebarWrapper>}
                </div>
            </div>
          </main>
        </div>
      </div>

      {/* Mobile Bottom Navigation Bar */}
      {mobile.isMobile && (
        <motion.nav
          className="fixed bottom-0 left-0 right-0 bg-slate-950/90 backdrop-blur-2xl border-t border-blue-300/20 shadow-[0_-18px_55px_rgba(37,99,235,0.18)] z-[100001]"
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        >
          {/* Main Navigation Items + notifications (always visible on mobile admin) */}
          <div className="flex items-center gap-1 px-1 py-3">
            <div className="flex flex-1 min-w-0 justify-evenly">
            {mobileNavigationItems.map((item) => {
              const shouldGlow = item.id === 'catalogue' && hasCompletedOnboarding && songsCount < 5;

              return (
              <motion.button
                key={item.id}
                className={`flex flex-col items-center space-y-1 p-2 rounded-2xl transition-all duration-200 flex-1 ${
                  currentPanel === item.path || 
                  (item.id === 'profile' && showProfileModal) ||
                  (item.id === 'catalogue' && showCatalogueModal) ||
                  (item.id === 'payouts' && showPayoutsModal)
                    ? 'text-blue-100 bg-blue-500/15 border border-blue-300/25 shadow-[0_0_24px_rgba(59,130,246,0.14)]'
                    : shouldGlow
                    ? 'text-blue-100 bg-blue-500/15 shadow-lg shadow-blue-500/30 animate-pulse'
                    : 'text-slate-400 hover:text-slate-200 border border-transparent'
                } ${item.id === 'catalogue' && catalogueGlow ? 'catalogue-glow' : ''}`}
                onClick={() => {
                  if (item.id === 'catalogue') {
                    handleCatalogueClick();
                  } else {
                    handleMobileNavigationClick(item);
                  }
                }}
                whileTap={{ scale: 0.95 }}
              >
                <span className={`${
                  currentPanel === item.path || 
                  (item.id === 'profile' && showProfileModal) ||
                  (item.id === 'catalogue' && showCatalogueModal) ||
                  (item.id === 'payouts' && showPayoutsModal)
                    ? 'text-blue-100 drop-shadow-[0_0_8px_rgba(59,130,246,0.55)]'
                    : 'text-slate-400 drop-shadow-[0_0_4px_rgba(148,163,184,0.25)]'
                }`}>
                  <item.icon className="w-5 h-5" />
                </span>
                <span className="text-xs font-medium">{item.label}</span>
                {(currentPanel === item.path || 
                  (item.id === 'profile' && showProfileModal) ||
                  (item.id === 'catalogue' && showCatalogueModal) ||
                  (item.id === 'payouts' && showPayoutsModal)) && (
                  <motion.div
                    className="w-1 h-1 rounded-full bg-blue-300 drop-shadow-[0_0_4px_rgba(59,130,246,0.8)]"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                  />
                )}
              </motion.button>
              );
            })}
            </div>
            <div className="flex-shrink-0 pr-1 scale-90 origin-center border-l border-white/10 pl-1">
              <UserNotificationCenter />
            </div>
          </div>
          <AnimatePresence>
            {showMoreMenu && (
              <>
                <motion.div
                  key="more-menu-backdrop"
                  className="fixed inset-0 z-[100000] bg-black/65"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  onClick={() => setShowMoreMenu(false)}
                  aria-hidden
                />
                <motion.div
                  key="more-menu-sheet"
                  className="absolute bottom-full left-0 right-0 z-[100002] rounded-t-[2rem] border-t border-blue-300/35 bg-slate-950 shadow-[0_-24px_60px_rgba(0,0,0,0.55)] ring-1 ring-white/10"
                  initial={{ 
                  y: 20, 
                  opacity: 0,
                  scale: 0.95,
                  borderRadius: "9999px"
                }}
                animate={{ 
                  y: 0, 
                  opacity: 1,
                  scale: 1,
                  borderRadius: "16px"
                }}
                exit={{ 
                  y: 20, 
                  opacity: 0,
                  scale: 0.95,
                  borderRadius: "9999px"
                }}
                transition={{ 
                  type: "spring",
                  damping: 25,
                  stiffness: 300,
                  duration: 0.3
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-4">
                  <div className="grid grid-cols-2 gap-3">
                    {moreMenuItems.map((item) => {
                      // Handle full-width competition button
                      if (item.isFullWidth) {
                        return (
                          <div key={item.id} className="col-span-3 mb-2">
                            <motion.button
                              className="w-full p-4 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transition-all duration-200 flex items-center justify-center space-x-3"
                              onClick={() => handleMoreMenuClick(item)}
                              whileTap={{ scale: 0.95 }}
                              whileHover={{ scale: 1.02 }}
                            >
                              <item.icon className="w-6 h-6 text-white" />
                              <div className="flex-1 overflow-hidden">
                                <div className="text-white font-bold text-lg whitespace-nowrap uppercase">
                                  {item.label}
                                </div>
                                {item.tickerText && (
                                  <div className="text-white/90 text-sm overflow-hidden relative">
                                    <motion.div
                                      animate={{ x: [0, -300] }}
                                      transition={{ 
                                        repeat: Infinity, 
                                        duration: 5, 
                                        ease: "linear" 
                                      }}
                                      className="whitespace-nowrap flex"
                                    >
                                      <span>{item.tickerText}</span>
                                      <span className="mx-8">{item.tickerText}</span>
                                      <span>{item.tickerText}</span>
                                    </motion.div>
                                  </div>
                                )}
                              </div>
                            </motion.button>
                          </div>
                        );
                      }
                      
                      // Melody: native <button> — motion.button + async open was unreliable on iOS Safari
                      if (item.id === 'melody' && !item.isComingSoon) {
                        return (
                          <button
                            type="button"
                            key={item.id}
                            className="relative z-10 flex flex-col items-center space-y-2 p-3 rounded-lg transition-all duration-200 touch-manipulation min-h-[48px] bg-blue-500/12 border border-blue-300/20 hover:bg-blue-500/18 hover:border-blue-300/35 shadow-[0_10px_28px_rgba(15,23,42,0.32)] active:scale-95"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleMoreMenuClick(item);
                            }}
                          >
                            <span className="transition-all duration-200 text-slate-200 drop-shadow-[0_0_6px_rgba(148,163,184,0.3)]">
                              <item.icon className="w-6 h-6" />
                            </span>
                            <span className="text-xs font-semibold text-center text-white">{item.label}</span>
                            <span className="text-[10px] text-blue-200/75 text-center leading-tight">Artist Support</span>
                          </button>
                        );
                      }

                      // Regular menu items
                      return (
                        <motion.button
                          type="button"
                          key={item.id}
                          className={`relative z-10 flex flex-col items-center space-y-2 p-3 rounded-lg transition-all duration-200 touch-manipulation ${
                            item.isComingSoon 
                              ? 'bg-white/[0.03] hover:bg-white/[0.03] cursor-not-allowed opacity-50'
                              : 'bg-blue-500/12 border border-blue-300/20 hover:bg-blue-500/18 hover:border-blue-300/35 shadow-[0_10px_28px_rgba(15,23,42,0.32)]'
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!item.isComingSoon) handleMoreMenuClick(item);
                          }}
                          whileTap={item.isComingSoon ? {} : { scale: 0.95 }}
                          whileHover={item.isComingSoon ? {} : { scale: 1.05 }}
                          disabled={item.isComingSoon}
                        >
                          <span className={`transition-all duration-200 ${
                            item.isComingSoon 
                              ? 'text-gray-500' 
                              : 'text-slate-200 drop-shadow-[0_0_6px_rgba(148,163,184,0.3)] hover:text-blue-200 hover:drop-shadow-[0_0_8px_rgba(59,130,246,0.55)]'
                          }`}>
                            <item.icon className="w-6 h-6" />
                          </span>
                          <span className={`text-xs font-semibold text-center ${
                            item.isComingSoon ? 'text-gray-500' : 'text-white'
                          }`}>{item.label}</span>
                          {item.isComingSoon && (
                            <span className="text-[10px] text-gray-500 text-center leading-tight">Coming Soon!</span>
                          )}
                        </motion.button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
              </>
            )}
          </AnimatePresence>
        </motion.nav>
      )}

      {/* Stop Receiving New Requests Button - Mobile */}
      {mobile.isMobile && currentPanel === '/admin/live-dashboard' && liveStatus.isLive && !isTrialMode && pendingRequestsCount >= 1 && (
        <motion.div
          className="fixed bottom-40 left-0 right-0 z-40 flex justify-center px-4"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300, delay: 0.1 }}
        >
          <Button
            onClick={async () => {
              if (!user) return;
              try {
                const liveStatusRef = doc(db, 'live_status', user.uid);
                const currentAccepting = liveStatus.accepting_new_requests !== false;
                
                if (currentAccepting) {
                  await updateDoc(liveStatusRef, {
                    accepting_new_requests: false,
                    requests_paused_at: serverTimestamp()
                  });
                  toast({ 
                    title: "New Requests Paused", 
                    description: "You're no longer accepting new requests. Complete existing requests before going offline." 
                  });
                } else {
                  await updateDoc(liveStatusRef, {
                    accepting_new_requests: true,
                    requests_paused_at: null
                  });
                  toast({ 
                    title: "Resuming New Requests", 
                    description: "You're now accepting new song requests again." 
                  });
                }
              } catch (error) {
                console.error('Error toggling accepting requests:', error);
                toast({ 
                  title: "Error", 
                  description: "Could not update request status.", 
                  variant: "destructive" 
                });
              }
            }}
            className={`w-full max-w-md ${liveStatus.accepting_new_requests === false ? 'bg-orange-600 hover:bg-orange-700' : 'bg-yellow-600 hover:bg-yellow-700'} text-white font-semibold py-3 rounded-lg shadow-lg`}
          >
            {liveStatus.accepting_new_requests === false ? 'Resume New Requests' : 'Pause New Requests'}
          </Button>
        </motion.div>
      )}

      {/* Floating Go Live Toggle for Mobile */}
      {mobile.isMobile && currentPanel === '/admin/live-dashboard' && !showOfflineCountdown && (
        <motion.div
          className="fixed bottom-24 left-0 right-0 z-40 flex justify-center"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300, delay: 0.2 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <div className={`backdrop-blur-sm border-2 rounded-full px-6 py-3 shadow-lg transition-all duration-1000 ${
            liveStatus.isLive 
              ? `bg-green-900/95 border-green-500/60 shadow-[0_4px_20px_rgba(34,197,94,0.3)] ${isTrialMode && goLivePillPulse ? 'animate-pulse' : ''}` 
              : isGoingLive
              ? 'bg-purple-900/95 border-purple-500/60 shadow-[0_4px_20px_rgba(168,85,247,0.5)] animate-pulse'
              : `bg-gray-900/95 border-purple-500/60 shadow-[0_4px_20px_rgba(168,85,247,0.3)] ${isTrialMode ? 'drop-shadow-[0_0_12px_rgba(168,85,247,0.9)] shadow-[0_0_20px_rgba(168,85,247,0.6)]' : ''} ${isTrialMode && goLivePillPulse ? 'animate-pulse' : ''}`
          }`}>
            <div className="flex items-center space-x-4">
                             <div className="flex items-center space-x-2">
                 <Radio className={`w-5 h-5 ${(liveStatus.isLive || isTrialMode) ? `text-green-400 ${isTrialMode ? 'drop-shadow-[0_0_8px_rgba(34,197,94,0.8)]' : ''}` : isGoingLive ? 'text-purple-400 animate-pulse' : `text-purple-400 ${isTrialMode ? 'drop-shadow-[0_0_8px_rgba(168,85,247,0.8)]' : ''}`}`} />
                 <span className={`text-sm font-semibold text-white ${isTrialMode && !liveStatus.isLive ? 'drop-shadow-[0_0_8px_rgba(168,85,247,0.8)]' : ''}`}>
                   {isGoingLive ? 'Starting...' : (liveStatus.isLive || isTrialMode) ? 'LIVE' : 'Go Live'}
                 </span>
                 {(liveStatus.isLive || isTrialMode) && (
                   <div className={`w-2 h-2 bg-green-400 rounded-full ${isTrialMode ? 'animate-pulse drop-shadow-[0_0_8px_rgba(34,197,94,0.8)]' : 'animate-pulse drop-shadow-[0_0_8px_rgba(34,197,94,0.8)]'}`} />
                 )}
                 {isGoingLive && !liveStatus.isLive && !isTrialMode && (
                   <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse drop-shadow-[0_0_8px_rgba(168,85,247,0.8)]" />
                 )}
                 {isTrialMode && !liveStatus.isLive && !isGoingLive && (
                   <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse drop-shadow-[0_0_8px_rgba(34,197,94,0.8)]" />
                 )}
               </div>
              <Switch
                className={`transition-all duration-1000 ${(liveStatus.isLive || isTrialMode) ? 'data-[state=checked]:bg-green-600' : 'data-[state=checked]:bg-purple-600'} ${isTrialMode ? 'drop-shadow-[0_0_12px_rgba(168,85,247,0.9)] shadow-[0_0_20px_rgba(168,85,247,0.6)]' : ''}`}
                checked={liveStatus.isLive || isTrialMode}
                disabled={isGoingLive}
                onCheckedChange={async (checked) => {
                  console.log('🔍 DEBUG: Mobile toggle clicked, checked:', checked, 'isTrialMode:', isTrialMode);
                  
                  // CRITICAL: Allow going offline even during trial mode (fallback)
                  if (!checked && isTrialMode) {
                    console.log('🔍 DEBUG: Going offline from trial mode');
                    // End trial mode and set offline
                    setIsTrialMode(false);
                    localStorage.removeItem('trial_mode_active');
                    localStorage.removeItem('go_live_pill_pulse');
                    
                    // Set offline in Firestore
                    if (user?.uid) {
                      try {
                        const liveStatusRef = doc(db, 'live_status', user.uid);
                        await updateDoc(liveStatusRef, {
                          is_live: false,
                          session_id: null,
                          session_end_time: serverTimestamp()
                        });
                        console.log('✅ Manually set offline from trial mode');
                      } catch (error) {
                        console.error('Error setting offline from trial:', error);
                        // Fallback if update fails
                        try {
                          await setDoc(doc(db, 'live_status', user.uid), {
                            is_live: false,
                            session_id: null
                          }, { merge: true });
                        } catch (fallbackError) {
                          console.error('Fallback offline setting failed:', fallbackError);
                        }
                      }
                    }
                    return;
                  }
                  
                  if (checked) {
                    // Immediate visual feedback
                    setIsGoingLive(true);
                    
                    // Check if songs exist OR if songs were just added (localStorage flag)
                    const songsAddedFlag = localStorage.getItem('ysr_songs_added_first_time') === 'true';
                    const hasSongsInState = Array.isArray(songs) && songs.length > 0;
                    
                    console.log('🔍 DEBUG: Checking songs for Go Live:', { 
                      hasSongsInState, 
                      songsLength: songs?.length,
                      songsAddedFlag,
                      loadingSongs 
                    });
                    
                    // CRITICAL: If songs were just added, clear any wizard triggers immediately
                    if (songsAddedFlag) {
                      localStorage.removeItem('ysr_show_song_wizard');
                    }
                    
                    // Only block if we're sure there are no songs AND the flag isn't set
                    if (!hasSongsInState && !songsAddedFlag && !loadingSongs) {
                      console.log('🚫 CRITICAL: No songs found and flag not set, blocking goLive');
                      setIsGoingLive(false);
                      setShowAddSongsModal(true);
                      return;
                    }
                    
                    // If songs were just added but not yet in state, proceed immediately (flag is set)
                    if (songsAddedFlag && !hasSongsInState) {
                      console.log('✅ Songs flag set - proceeding to Go Live immediately');
                      setIsGoingLive(false);
                      navigate('/admin/live-dashboard?showStartModal=true');
                      return;
                    }
                    
                    // Navigate to live dashboard and let it show the session duration modal
                    console.log('🔍 DEBUG: Navigating to live dashboard to show session duration modal');
                    setIsGoingLive(false);
                    navigate('/admin/live-dashboard?showStartModal=true');
                  } else {
                    if (mobile.isMobile) {
                      // Immediately show offline state for better UX
                      console.log('🔍 DEBUG: Starting offline countdown');
                      startOfflineCountdown(); // Start the animated countdown
                    } else {
                      navigate('/admin/live-dashboard?offline=true'); // Desktop fallback
                    }
                  }
                }}
                data-live-toggle
              />
            </div>
          </div>
        </motion.div>
      )}


      {/* Profile Settings Modal */}
      <MobileAnimatedModal
        isOpen={mobile.isMobile && showProfileModal}
        onClose={() => setShowProfileModal(false)}
        title="Profile Settings"
        icon={User}
        position="bottom"
        bottomInset="152px"
        maxWidth="md"
      >
        <div className="space-y-4">
          <div className="text-center mb-4">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3 overflow-hidden">
              {profile?.profile_picture_url ? (
                <img
                  src={profile.profile_picture_url}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-2xl">🎭</span>
              )}
            </div>
            <h4 className="text-white font-semibold">{profile?.artist_name || 'Artist'}</h4>
            <p className="text-gray-400 text-sm">{profile?.email}</p>
          </div>
          
          <div className="space-y-3">
            <button
              onClick={() => {
                setShowProfileModal(false);
                handlePanelChange('/admin/profile');
              }}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg text-white hover:bg-gray-700/50 transition-colors text-left"
            >
              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-purple-400" />
                <div>
                  <div className="font-medium">Edit Profile</div>
                  <div className="text-sm text-gray-400">Update your artist information</div>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => {
                setShowProfileModal(false);
                handlePanelChange('/admin/profile?tab=alerts');
              }}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg text-white hover:bg-gray-700/50 transition-colors text-left"
            >
              <div className="flex items-center space-x-3">
                <Bell className="w-5 h-5 text-amber-400" />
                <div>
                  <div className="font-medium">Turn On Notifications</div>
                  <div className="text-sm text-gray-400">Never miss a request</div>
                </div>
              </div>
            </button>

            <button
              onClick={() => {
                setShowProfileModal(false);
                window.open(`https://yoursongrequest.com/${profile?.url_slug}`, '_blank');
              }}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg text-white hover:bg-gray-700/50 transition-colors text-left"
            >
              <div className="flex items-center space-x-3">
                <Globe className="w-5 h-5 text-blue-400" />
                <div>
                  <div className="font-medium">View Public Page</div>
                  <div className="text-sm text-gray-400">See how fans see your page</div>
                </div>
              </div>
            </button>
            
            <button
              onClick={() => {
                setShowProfileModal(false);
                setShowLogoutConfirm(true);
              }}
              className="w-full px-4 py-3 bg-red-900/50 border border-red-600 rounded-lg text-red-300 hover:bg-red-800/50 transition-colors text-left"
            >
              <div className="flex items-center space-x-3">
                <LogOut className="w-5 h-5 text-red-400" />
                <div>
                  <div className="font-medium">Sign Out</div>
                  <div className="text-sm text-red-400">Log out of your account</div>
                </div>
              </div>
            </button>
          </div>
        </div>
      </MobileAnimatedModal>

      {/* Catalogue Management Modal */}
      <MobileAnimatedModal
        isOpen={mobile.isMobile && showCatalogueModal}
        onClose={() => setShowCatalogueModal(false)}
        title="Catalogue Management"
        icon={Music}
        position="bottom"
        maxWidth="md"
      >
        <div className="space-y-4">
          <div className="text-center mb-4">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-2xl">🎵</span>
            </div>
            <h4 className="text-white font-semibold">Song Catalogue</h4>
            <p className="text-gray-400 text-sm">{songsCount} songs available</p>
          </div>
          
          <div className="space-y-3">
            <button
              onClick={() => {
                setShowCatalogueModal(false);
                handlePanelChange('/admin/catalogue');
              }}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg text-white hover:bg-gray-700/50 transition-colors text-left"
            >
              <div className="flex items-center space-x-3">
                <Music className="w-5 h-5 text-purple-400" />
                <div>
                  <div className="font-medium">Manage Songs</div>
                  <div className="text-sm text-gray-400">Add, edit, or remove songs</div>
                </div>
              </div>
            </button>
            
            <button
              onClick={() => {
                setShowCatalogueModal(false);
                handlePanelChange('/admin/catalogue?tab=setlists');
              }}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg text-white hover:bg-gray-700/50 transition-colors text-left"
            >
              <div className="flex items-center space-x-3">
                <ListMusic className="w-5 h-5 text-green-400" />
                <div>
                  <div className="font-medium">Manage Setlists</div>
                  <div className="text-sm text-gray-400">Create and organize setlists</div>
                </div>
              </div>
            </button>
            
            <button
              onClick={() => {
                setShowCatalogueModal(false);
                handlePanelChange('/admin/calendar');
              }}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg text-white hover:bg-gray-700/50 transition-colors text-left"
            >
              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-blue-400" />
                <div>
                  <div className="font-medium">Schedule Gigs</div>
                  <div className="text-sm text-gray-400">Plan your performances</div>
                </div>
              </div>
            </button>
          </div>
        </div>
      </MobileAnimatedModal>

      {/* Songs Required Modal */}
      <AddSongsRequiredModal
        isOpen={showAddSongsModal}
        onClose={() => setShowAddSongsModal(false)}
        onNavigateToCatalogue={() => {
          setShowAddSongsModal(false);
          handlePanelChange('/admin/catalogue');
        }}
      />

      {/* Payouts Overview Modal */}
      <MobileAnimatedModal
        isOpen={mobile.isMobile && showPayoutsModal}
        onClose={() => setShowPayoutsModal(false)}
        title="Payouts & Earnings"
        icon={Banknote}
        position="bottom"
        maxWidth="md"
      >
        <div className="space-y-4">
          <div className="text-center mb-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-2xl">💰</span>
            </div>
            <h4 className="text-white font-semibold">Earnings Overview</h4>
            <p className="text-gray-400 text-sm">Track your performance income</p>
          </div>
          
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="text-center p-3 bg-green-900/20 border border-green-700/40 rounded-lg">
              <div className="text-lg font-bold text-green-400">{format(totalEarnings)}</div>
              <div className="text-xs text-gray-400">Total Earnings</div>
            </div>
            <div className="text-center p-3 bg-blue-900/20 border border-blue-700/40 rounded-lg">
              <div className="text-lg font-bold text-blue-400">{format(availableToRequest)}</div>
              <div className="text-xs text-gray-400">Available to Request</div>
            </div>
          </div>
          
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => {
                setShowPayoutsModal(false);
                handlePanelChange('/admin/payouts?focus=request');
              }}
              className="w-full px-4 py-3 bg-purple-900/30 border border-purple-600/60 rounded-lg text-white hover:bg-purple-900/45 transition-colors text-left"
            >
              <div className="flex items-center space-x-3">
                <Wallet className="w-5 h-5 text-purple-300 shrink-0" />
                <div>
                  <div className="font-medium">Request payment</div>
                  <div className="text-sm text-gray-400">
                    UK bank transfer — open the payout request form
                  </div>
                </div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => {
                setShowPayoutsModal(false);
                handlePanelChange('/admin/payouts');
              }}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg text-white hover:bg-gray-700/50 transition-colors text-left"
            >
              <div className="flex items-center space-x-3">
                <Banknote className="w-5 h-5 text-green-400" />
                <div>
                  <div className="font-medium">View detailed payouts</div>
                  <div className="text-sm text-gray-400">Full earnings overview and history</div>
                </div>
              </div>
            </button>
          </div>
        </div>
      </MobileAnimatedModal>

      {/* Quick Tour Modal */}
      <MobileAnimatedModal
        isOpen={mobile.isMobile && showQuickTourModal}
        onClose={() => setShowQuickTourModal(false)}
        title="Quick Tour"
        icon={Target}
        position="center"
        maxWidth="md"
      >
        <div className="space-y-4">
          <div className="text-center mb-4">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-2xl">🎯</span>
            </div>
            <h4 className="text-white font-semibold">Welcome to Your Artist Dashboard!</h4>
            <p className="text-gray-400 text-sm">Let's get you started with a quick tour</p>
          </div>
          
          <div className="space-y-3">
            <div className="p-3 bg-blue-900/20 border border-blue-700/40 rounded-lg">
              <div className="flex items-center space-x-3">
                <Radio className="w-5 h-5 text-blue-400" />
                <div>
                  <div className="font-medium text-blue-300">Go Live</div>
                  <div className="text-sm text-blue-400">Start performing and receive song requests</div>
                </div>
              </div>
            </div>
            
            <div className="p-3 bg-green-900/20 border border-green-700/40 rounded-lg">
              <div className="flex items-center space-x-3">
                <Music className="w-5 h-5 text-green-400" />
                <div>
                  <div className="font-medium text-green-300">Manage Catalogue</div>
                  <div className="text-sm text-green-400">Add songs and create setlists</div>
                </div>
              </div>
            </div>
            
            <div className="p-3 bg-purple-900/20 border border-purple-700/40 rounded-lg">
              <div className="flex items-center space-x-3">
                <Banknote className="w-5 h-5 text-purple-400" />
                <div>
                  <div className="font-medium text-purple-300">Track Earnings</div>
                  <div className="text-sm text-purple-400">Monitor your performance income</div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex space-x-3 pt-4">
            <button
              onClick={() => setShowQuickTourModal(false)}
              className="flex-1 px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-700/50 transition-colors"
            >
              Skip Tour
            </button>
            <button
              onClick={() => {
                setShowQuickTourModal(false);
                // Navigate to live dashboard and trigger onboarding immediately
                navigate('/admin/live-dashboard?tour=true');
                // Set a flag to trigger onboarding on the live dashboard
                localStorage.setItem('ysr_trigger_onboarding', 'true');
              }}
              className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg text-white font-semibold transition-colors"
            >
              Start Tour
            </button>
          </div>
        </div>
      </MobileAnimatedModal>

      {/* Mobile Offline Countdown */}
      <MobileOfflineCountdown
        isVisible={mobile.isMobile && showOfflineCountdown}
        countdown={offlineCountdown}
        onCancel={cancelOfflineCountdown}
      />

      {/* Desktop Offline Countdown */}
      {!mobile.isMobile && showOfflineCountdown && (
        <div className="fixed top-4 right-4 z-[60] bg-red-900/95 backdrop-blur-sm border-2 border-red-500/60 rounded-xl p-4 shadow-[0_8px_32px_rgba(239,68,68,0.3)]">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <Radio className="w-5 h-5 text-orange-400" />
                <span className="text-white font-semibold">Going Offline in</span>
              </div>
              <div className="flex items-center space-x-1">
                {[5, 4, 3, 2, 1].map((num) => (
                  <div
                    key={num}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                      offlineCountdown >= num
                        ? 'bg-orange-500 text-white'
                        : 'bg-orange-500/30 text-orange-200/50'
                    }`}
                  >
                    {num}
                  </div>
                ))}
              </div>
            </div>
            <Button
              onClick={cancelOfflineCountdown}
              className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 text-sm"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Bottom Safe Area for Mobile */}
      {mobile.isMobile && (
        <div className="h-24" /> // Safe area for mobile navigation
      )}

    </>
  );
};

export default AdminDashboard;